import { useLazyQuery, useQuery } from "@apollo/client/react";
import { size, sortBy, uniqBy, isEmpty, filter } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import { pagedSearchTags, getTagDetails } from "src/queries/tags.graphql";
import { TAG_LOAD_LIMIT } from "src/util/consts";

type TagItem = {
  id: string;
  displayValue?: string;
  type?: string;
  [key: string]: unknown;
};
type TagBucket = { tags?: TagItem[]; totalCount?: number | undefined };
type TagsState = Record<string, TagBucket | undefined>;

const getUniqueTags = (tags: TagItem[]): TagItem[] =>
  sortBy(uniqBy(tags, "id"), "displayValue");

const getOffset = ({
  initialLoad,
  pagedTags,
  tagType,
}: {
  initialLoad: boolean;
  pagedTags: TagsState;
  tagType: string;
}): number => (!initialLoad ? size(pagedTags[tagType]?.tags ?? []) : 0);

const useLoadTags = ({
  activeTagId,
  isSelected,
  tagTypesConfig,
}: {
  activeTagId?: string | undefined;
  isSelected?: boolean | undefined;
  tagTypesConfig?: Record<string, unknown> | undefined;
}) => {
  const isActiveTagTypeId = !!(
    activeTagId && tagTypesConfig?.[activeTagId] != null
  );
  const [tagsLoading, setTagsLoading] = useState<Record<string, boolean>>({});
  const [allTags, setAllTags] = useState<TagsState>({});
  const [pagedTags, setPagedTags] = useState<TagsState>({});
  const pagedTagsRef = useRef(pagedTags);

  pagedTagsRef.current = pagedTags;

  const { data: tagData } = useQuery(getTagDetails, {
    variables: {
      tagId: activeTagId,
    },
    skip: !activeTagId || !isSelected || isActiveTagTypeId,
  });

  const [lazyLoadTags] = useLazyQuery(pagedSearchTags);

  // Ignore cached tag details when the route is a tag-type explore id.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeTag = isActiveTagTypeId
    ? undefined
    : (((tagData as any)?.tag || [])[0] as TagItem | undefined);
  const activeTagRef = useRef(activeTag);
  activeTagRef.current = activeTag;

  const loadTags = useCallback(
    (tagType: string, initialLoad = false) => {
      setTagsLoading((prev) => ({ ...prev, [tagType]: true }));

      const offset = getOffset({
        initialLoad,
        pagedTags: pagedTagsRef.current,
        tagType,
      });

      return lazyLoadTags({
        variables: {
          input: {
            filter: { tagTypes: [tagType] },
            offset,
            limit: TAG_LOAD_LIMIT,
          },
          showAppliedCount: true,
        },
      })
        .then(({ data }) => {
          if (!data) {
            setTagsLoading((prev) => ({ ...prev, [tagType]: false }));
            return;
          }

          setTagsLoading((prev) => ({ ...prev, [tagType]: false }));

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const results = (data as any)?.results as
            | { tags?: TagItem[]; totalCount?: number }
            | undefined;
          const newTags = results?.tags ?? [];
          const totalCount = results?.totalCount;
          const currentActiveTag = activeTagRef.current;

          setPagedTags((prev) => ({
            ...prev,
            [tagType]: {
              tags: getUniqueTags([...(prev[tagType]?.tags ?? []), ...newTags]),
              totalCount,
            },
          }));

          setAllTags((prev) => ({
            ...prev,
            [tagType]: {
              tags: getUniqueTags([
                ...(prev[tagType]?.tags ?? []),
                ...newTags,
                ...filter(currentActiveTag ? [currentActiveTag] : [], {
                  type: tagType,
                }),
              ]),
              totalCount,
            },
          }));
        })
        .catch((err: unknown) => {
          if ((err as { name?: string })?.name === "AbortError") {
            setTagsLoading((prev) => ({ ...prev, [tagType]: false }));
            return;
          }
          setTagsLoading((prev) => ({ ...prev, [tagType]: false }));
          throw err;
        });
    },
    [lazyLoadTags]
  );

  const loadTagsRef = useRef(loadTags);
  loadTagsRef.current = loadTags;

  useEffect(() => {
    const tagType = activeTag?.type;
    if (!tagType || !isSelected) {
      return;
    }
    if (!isEmpty(pagedTagsRef.current[tagType]?.tags)) {
      return;
    }
    loadTags(tagType);
  }, [activeTag?.type, isSelected, loadTags]);

  return {
    tags: allTags,
    activeTag,
    loadTags,
    tagsLoading,
  };
};

export default useLoadTags;
